import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AuthService, CmsService, CurrencyService, GlobalMessageService, GlobalMessageType, RoutingService } from '@spartacus/core';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { KnBrCommonService } from 'src/services/kn-br-common.service';
import { KnBrCustomerService } from '../../../store/kn-br-customer/kn-br-customer.service';

import { UserAccountFacade } from 'src/feature-libs/kn-br-user/kn-br-account/root/facade';
import { CustomerData, SalesArea } from 'src/feature-libs/kn-br-user/kn-br-account/root/model/customer.model';
import { KnBrSidebarService } from '../../../services/kn-br-sidebar.service';
import { KnBrCustomerContextService } from '../../../store/kn-br-customer-context/kn-br-customer-context.service';
import { KnBrCustomerContextAdapter } from '../../../store/kn-br-customer-context/kn-br-customer-context.adapter';

@Component({
    selector: 'kn-br-outlet',
    templateUrl: './kn-br-outlet.component.html',
    styleUrls: ['./kn-br-outlet.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class KnBrOutletComponent implements OnInit, OnDestroy {
  isHeaderHidden = false;
  customerContext$: Observable<CustomerData> = this.customerContextService.get$;
  isLoggedIn$: Observable<boolean> = this.authService.isUserLoggedIn();
  redirectionDetail$: Observable<any> = this.knBrCommonService.redirectionDetail$;
  user$ = this.userService.get();
  selectedCustomer;
  subscription = new Subscription();
  readonly isLoginPage$ = this.cmsService
    .getCurrentPage()
    .pipe(
      map(
        (page) =>
          page &&
          (page.template === 'KnBrLoginPageTemplate' ||
            page.template === 'knBrQuoteSellerApproverTemplate' ||
            page.template === 'KnBrRegistrationPageTemplate')
      )
    );
  readonly isStaticPage$ = this.cmsService
    .getCurrentPage()
    .pipe(map((page) => page && page.template === 'ContentPage1Template'));
  readonly hasBottomHeaderSlot$ = combineLatest([
    this.cmsService.getContentSlot('BottomHeaderSlot'),
    this.cmsService.getCurrentPage(),
  ]).pipe(
    map(
      ([slot, page]) =>
        !!slot?.components?.length &&
        page?.template !== 'LandingPage2Template' &&
        page?.template !== 'KnBrLandingPage2Template' &&
        page?.template !== 'MultiStepCheckoutSummaryPageTemplate' &&
        page?.template !== 'KnBrOrderConfirmationPageTemplate'
    )
  );
  readonly hasHeaderRow$ = combineLatest([
    this.cmsService.getContentSlot('NavigationBar'),
    this.cmsService.getContentSlot('MiniCart'),
    this.hasBottomHeaderSlot$,
  ]).pipe(
    map(
      ([navSlot, cartSlot, hasBottomHeaderSlot]) =>
        !!navSlot?.components?.length || !!cartSlot?.components?.length || hasBottomHeaderSlot
    )
  );
  constructor(
    private cmsService: CmsService,
    private customerContextService: KnBrCustomerContextService,
    private userService: UserAccountFacade,
    private sidebarService: KnBrSidebarService,
    private knBrCommonService: KnBrCommonService,
    private authService: AuthService,
    private routingService: RoutingService,
    private customerSearchService: KnBrCustomerService,
    private customerContextAdapter: KnBrCustomerContextAdapter,
    private globalMessageService: GlobalMessageService,
    private currencyService: CurrencyService
  ) {}

  removeCustomerContext() {
    this.customerContextService.delete();
    this.routingService.go({ cxRoute: 'switchAccount' });
  }

  ngOnInit() {
    this.subscription.add(
      this.isLoggedIn$.subscribe((res) => {
        if (res) {
          this.knBrCommonService.getRedirectionDetail().subscribe((redirect) => {
            this.knBrCommonService.redirectionDetail$.next(redirect);
          });
        }
      })
    );
    this.subscription.add(
      this.user$.subscribe((user: any) => {
        if (user && user.customerId) {
          this.customerContextService.set({ customerName: user.customerName, customerNumber: user.customerId });
        }
      })
    );
    this.subscription.add(
      combineLatest([this.customerSearchService.list$, this.customerSearchService.searchSubmitted$]).subscribe(
        ([customerList, isSearchSubmitted]) => {
          if (isSearchSubmitted) {
            // A real user-initiated search supersedes the "select a division" redirect message.
            this.customerContextService.clearContextRequiredRedirect();
          }
          if (customerList && customerList.length > 0 && this.selectedCustomer) {
            const customertosetcontext = customerList.find(
              (customer) => customer.customerNumber === this.selectedCustomer.customerNumber
            );
            if (customertosetcontext) {
              this.customerContextService.set(customertosetcontext);

              const defaultSelected = customertosetcontext.salesAreaList?.find(
                (sa: SalesArea) => sa.selected === true
              );
              const selectedSalesArea =
                defaultSelected?.salesArea ??
                (customertosetcontext.salesAreaList?.length === 1
                  ? customertosetcontext.salesAreaList[0].salesArea
                  : null);

              if (selectedSalesArea) {
                const fullSalesAreaObject = customertosetcontext.salesAreaList?.find(
                  (sa: SalesArea) => sa.salesArea === selectedSalesArea
                );
                const currency = fullSalesAreaObject?.currency ?? 'USD';

                this.customerContextAdapter.setSalesArea(selectedSalesArea);
                this.customerContextAdapter.setCurrency(currency);
                this.currencyService.setActive(currency);
                this.customerContextService.clearContextRequiredRedirect();
              }
            }
          } else if (
            (!customerList || customerList.length === 0) &&
            !isSearchSubmitted &&
            !this.customerContextService.isContextRequiredRedirectPending()
          ) {
            this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_ERROR);
          }
        }
      )
    );
    this.subscription.add(
      this.customerContextService.get$.subscribe((customerdata) => {
        this.selectedCustomer = customerdata;
        if (customerdata && !Object.prototype.hasOwnProperty.call(customerdata, 'telephone')) {
          this.customerSearchService.search(null);
        }
      })
    );
  }

  getSelectedSalesAreaName(customer: CustomerData): string {
    if (!customer?.salesAreaList?.length) {
      return '';
    }
    const activeCode = this.customerContextAdapter.getSalesArea();
    const activeSalesArea =
      customer.salesAreaList.find((sa: SalesArea) => sa.salesArea === activeCode) ??
      customer.salesAreaList.find((sa: SalesArea) => sa.selected === true);
    return activeSalesArea?.accountName ?? '';
  }

  logout() {
    this.authService.logout();
    this.knBrCommonService.setLoggedIn(false);
  }

  changeMenuState() {
    this.sidebarService.toggleMenuState();
  }

  ngOnDestroy() {
    this.selectedCustomer = undefined;
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
